import { useEffect, useRef, useState } from 'react';
import { Check, Copy } from 'lucide-react';

type ReferralLinkFieldProps = {
  referralLink?: string | null;
  fallbackText: string;
  copyLabel: string;
  compact?: boolean;
};

export const ReferralLinkField = ({
  referralLink,
  fallbackText,
  copyLabel,
  compact = false,
}: ReferralLinkFieldProps) => {
  const [isCopied, setIsCopied] = useState(false);
  const resetTimeoutRef = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      if (resetTimeoutRef.current !== null) {
        window.clearTimeout(resetTimeoutRef.current);
      }
    };
  }, []);

  const handleCopy = async () => {
    if (!referralLink || !navigator.clipboard?.writeText) {
      return;
    }

    await navigator.clipboard.writeText(referralLink);
    setIsCopied(true);

    if (resetTimeoutRef.current !== null) {
      window.clearTimeout(resetTimeoutRef.current);
    }

    resetTimeoutRef.current = window.setTimeout(() => {
      setIsCopied(false);
    }, 2000);
  };

  return (
    <div className="flex items-start justify-between gap-2">
      <span className={`min-w-0 flex-1 break-all ${compact ? 'text-xs text-gray-500' : 'text-sm text-gray-700'}`}>
        {referralLink || fallbackText}
      </span>

      {referralLink ? (
        <div className="flex shrink-0 items-center gap-1">
          <button
            type="button"
            onClick={handleCopy}
            className={`rounded-md text-gray-500 transition-colors hover:bg-gray-100 hover:text-[#026857] ${compact ? 'p-1' : 'p-1.5'}`}
            aria-label={copyLabel}
          >
            {isCopied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
          </button>

          {isCopied ? <span className="text-xs font-medium text-[#026857]">Copied!</span> : null}
        </div>
      ) : null}
    </div>
  );
};
